import { inject, Injectable } from '@angular/core';
import { ILocalSearchGroup } from './fi-local-search';
import { FiLocalSearchPropertyType } from './fi-local-search-property-type';
import { ILightWeightObjectCollection, ILocalSearchIdName, ILocalSearchIdNameWithPicture } from '@fi-shared/models';
import { fiGetMediaBankFileIconAwesome, getObjectTypeNameTextId } from '@fi-shared/utils';
import { AppTextService } from '@fi-shared/app-text';
import { compareWithSearchText } from '../utils/compare-with-search-text';
import { IAddedObject } from './fi-local-search.d';
import { IUserProfile } from '@fi-shared/profile-icon/profile-icon.component';
import { FileType, ObjectTypes } from '@fi-shared/types';
import { ILocalSearchParameter } from '../models/models';

interface IPropertyMappingConfig {
    modelProperty: keyof ILocalSearchParameter;
    valueType: 'id' | 'name';
}

interface ILabelTextConfig {
    singular: string;
    plural: string;
}

@Injectable()
export class FiLocalSearch2HelperService {
    private readonly maxGroupItemsCount = 2;
    private readonly appTextService = inject(AppTextService);

    private readonly labelTextConfig: Map<FiLocalSearchPropertyType, ILabelTextConfig> = new Map([
        [FiLocalSearchPropertyType.Names, { singular: 'common.name', plural: 'common.names' }],
        [FiLocalSearchPropertyType.Creators, { singular: 'common.created_by', plural: 'common.creators' }],
        [FiLocalSearchPropertyType.Tags, { singular: 'common.tags', plural: 'common.tags' }],
        [
            FiLocalSearchPropertyType.BusinessAreas,
            { singular: 'common.business_areas', plural: 'common.business_areas' },
        ],
        [FiLocalSearchPropertyType.Roles, { singular: 'common.role', plural: 'common.role' }],
        [FiLocalSearchPropertyType.BusinessUnits, { singular: 'common.units', plural: 'common.units' }],
        [FiLocalSearchPropertyType.Areas, { singular: 'common.areas', plural: 'common.areas' }],
        [FiLocalSearchPropertyType.Clusters, { singular: 'common.clusters', plural: 'common.clusters' }],
        [
            FiLocalSearchPropertyType.EmailAddressContacts,
            { singular: 'common.email_address', plural: 'common.email_address' },
        ],
        [
            FiLocalSearchPropertyType.PhoneNumberContacts,
            { singular: 'common.phone_number', plural: 'common.phone_numbers' },
        ],
        [FiLocalSearchPropertyType.Addresses, { singular: 'common.address', plural: 'common.address' }],
        [FiLocalSearchPropertyType.Content, { singular: 'common.content', plural: 'common.content' }],
        [FiLocalSearchPropertyType.ObjectIds, { singular: 'history.object_id', plural: 'history.object_id' }],
        [FiLocalSearchPropertyType.ObjectTypeNames, { singular: 'history.object_type', plural: 'history.object_type' }],
        [FiLocalSearchPropertyType.Checklists, { singular: 'checklists', plural: 'checklists' }],
        [FiLocalSearchPropertyType.MediaBankFileExtensions, { singular: 'common.format', plural: 'common.format' }],
        [FiLocalSearchPropertyType.Checkpoints, { singular: 'checklist.checkpoints', plural: 'checklist.checkpoints' }],
    ]);

    private readonly dataFieldValueLabels: Map<ObjectTypes, string> = new Map([
        [ObjectTypes.BusinessUnit, 'unit_details'],
        [ObjectTypes.User, 'user_details'],
    ]);

    private readonly propertyIconConfig: Map<FiLocalSearchPropertyType, string> = new Map([
        [FiLocalSearchPropertyType.Creators, 'fas fa-user-circle'],
        [FiLocalSearchPropertyType.Tags, 'fas fa-tag'],
        [FiLocalSearchPropertyType.Content, 'fas fa-highlighter'],
    ]);

    private readonly propertyRegularIconConfig: Map<FiLocalSearchPropertyType, string> = new Map([
        [FiLocalSearchPropertyType.Creators, 'far fa-user-circle'],
        [FiLocalSearchPropertyType.Tags, 'far fa-tag'],
        [FiLocalSearchPropertyType.Content, 'fal fa-pen'],
    ]);

    private readonly propertyColorConfig: Map<FiLocalSearchPropertyType, string> = new Map([
        [FiLocalSearchPropertyType.Creators, '#24958A'],
        [FiLocalSearchPropertyType.Tags, '#09477E'],
        [FiLocalSearchPropertyType.Content, '#EF940C'],
    ]);

    private readonly propertyMappingConfig: Map<FiLocalSearchPropertyType, IPropertyMappingConfig> = new Map([
        [FiLocalSearchPropertyType.Names, { modelProperty: 'Names', valueType: 'name' }],
        [FiLocalSearchPropertyType.Tags, { modelProperty: 'TagIds', valueType: 'id' }],
        [FiLocalSearchPropertyType.BusinessAreas, { modelProperty: 'BusinessAreaIds', valueType: 'id' }],
        [FiLocalSearchPropertyType.Roles, { modelProperty: 'RoleIds', valueType: 'id' }],
        [FiLocalSearchPropertyType.BusinessUnits, { modelProperty: 'BusinessUnitIds', valueType: 'id' }],
        [FiLocalSearchPropertyType.Areas, { modelProperty: 'AreaIds', valueType: 'id' }],
        [FiLocalSearchPropertyType.Clusters, { modelProperty: 'ClusterIds', valueType: 'id' }],
        [FiLocalSearchPropertyType.PhoneNumberContacts, { modelProperty: 'Contacts', valueType: 'name' }],
        [FiLocalSearchPropertyType.EmailAddressContacts, { modelProperty: 'Contacts', valueType: 'name' }],
        [FiLocalSearchPropertyType.Addresses, { modelProperty: 'Addresses', valueType: 'name' }],
        [FiLocalSearchPropertyType.DataFieldValues, { modelProperty: 'DataFieldValues', valueType: 'name' }],
        [FiLocalSearchPropertyType.Checklists, { modelProperty: 'ChecklistNames', valueType: 'name' }],
        [FiLocalSearchPropertyType.Checkpoints, { modelProperty: 'CheckpointNames', valueType: 'name' }],
        [FiLocalSearchPropertyType.ObjectIds, { modelProperty: 'ObjectIds', valueType: 'id' }],
        [FiLocalSearchPropertyType.ObjectTypeNames, { modelProperty: 'ObjectTypes', valueType: 'id' }],
        [
            FiLocalSearchPropertyType.MediaBankFileExtensions,
            { modelProperty: 'MediaBankFileExtensions', valueType: 'name' },
        ],
        [FiLocalSearchPropertyType.Content, { modelProperty: 'ContentSearchTexts', valueType: 'name' }],
    ]);

    readonly noSeeMatchingGroups: FiLocalSearchPropertyType[] = [
        FiLocalSearchPropertyType.Tags,
        FiLocalSearchPropertyType.BusinessAreas,
        FiLocalSearchPropertyType.Roles,
        FiLocalSearchPropertyType.BusinessUnits,
        FiLocalSearchPropertyType.Areas,
        FiLocalSearchPropertyType.Clusters,
        FiLocalSearchPropertyType.ObjectIds,
        FiLocalSearchPropertyType.ObjectTypeNames,
        FiLocalSearchPropertyType.MediaBankFileExtensions,
        FiLocalSearchPropertyType.Content,
    ];
    readonly objectTypesWithIdSearch: ObjectTypes[] = [
        ObjectTypes.Checklist2,
        ObjectTypes.MediaBank,
        ObjectTypes.Document,
        ObjectTypes.Template,
        ObjectTypes.Form,
        ObjectTypes.Forum,
        ObjectTypes.CheckList,
        ObjectTypes.Checklist2Template,
        ObjectTypes.SiteMap,
        ObjectTypes.History,
        ObjectTypes.Tag,
    ];

    getUpdatedLocalSearchGroups(
        searchText: string,
        lightWeightObjectCollection: ILightWeightObjectCollection,
        searchByContent: boolean,
    ): ILocalSearchGroup[] {
        const localSearchData: ILocalSearchGroup[] = [];
        const addLocalSearchGroupFn = (propertyType: FiLocalSearchPropertyType, items: ILocalSearchIdName[]) => {
            if (!items?.length) return;
            const groupItems = this.showSeeMatching(searchText, propertyType)
                ? items.slice(0, this.maxGroupItemsCount)
                : items.slice();
            localSearchData.push({
                propertyType: propertyType,
                items: groupItems,
                allItemsCount: items.length,
            });
        };

        if (lightWeightObjectCollection) {
            // backend can return lightWeightObjectCollection.Names with pictures (ex. insert media dialog)
            // or can return lightWeightObjectCollection.NamesStr array of strings (ex. history list)
            // so we should check both properties and return normalized data
            const allNames = lightWeightObjectCollection.Names;
            const allNamesStr: ILocalSearchIdName[] = lightWeightObjectCollection.NamesStr.map((name) => ({
                Id: undefined,
                Name: name,
            }));
            addLocalSearchGroupFn(
                FiLocalSearchPropertyType.Names,
                allNames.concat(allNamesStr).filter((nameV2) => this.filterLocalSearchItem(searchText, nameV2.Name)),
            );
        }

        if (searchByContent) {
            addLocalSearchGroupFn(FiLocalSearchPropertyType.Content, [{ Id: 1, Name: searchText }]);
        }

        if (lightWeightObjectCollection) {
            const allCreators = lightWeightObjectCollection.Creators;
            addLocalSearchGroupFn(
                FiLocalSearchPropertyType.Creators,
                allCreators.filter((item) => this.filterLocalSearchItem(searchText, item.Name)),
            );

            const allTags = lightWeightObjectCollection.Tags;
            addLocalSearchGroupFn(
                FiLocalSearchPropertyType.Tags,
                allTags.filter((item) => this.filterLocalSearchItem(searchText, item.Name)),
            );

            const businessAreas = lightWeightObjectCollection.BusinessAreas.filter((item) =>
                this.filterLocalSearchItem(searchText, item.Name),
            );
            addLocalSearchGroupFn(FiLocalSearchPropertyType.BusinessAreas, businessAreas);

            const roles = lightWeightObjectCollection.Roles.filter((item) =>
                this.filterLocalSearchItem(searchText, item.Name),
            );
            addLocalSearchGroupFn(FiLocalSearchPropertyType.Roles, roles);

            const businessUnits = lightWeightObjectCollection.BusinessUnits.filter((item) =>
                this.filterLocalSearchItem(searchText, item.Name),
            );
            addLocalSearchGroupFn(FiLocalSearchPropertyType.BusinessUnits, businessUnits);

            const areas = lightWeightObjectCollection.Areas.filter((item) =>
                this.filterLocalSearchItem(searchText, item.Name),
            );
            addLocalSearchGroupFn(FiLocalSearchPropertyType.Areas, areas);

            const clusters = lightWeightObjectCollection.Clusters.filter((item) =>
                this.filterLocalSearchItem(searchText, item.Name),
            );
            addLocalSearchGroupFn(FiLocalSearchPropertyType.Clusters, clusters);

            const checklists = lightWeightObjectCollection.Checklists.filter((item) =>
                this.filterLocalSearchItem(searchText, item.Name),
            );
            addLocalSearchGroupFn(FiLocalSearchPropertyType.Checklists, checklists);

            const emailAddressContacts = lightWeightObjectCollection.EmailAddressContacts.filter((item) =>
                this.filterLocalSearchItem(searchText, item),
            ).map((item) => ({ Id: undefined, Name: item }));
            addLocalSearchGroupFn(FiLocalSearchPropertyType.EmailAddressContacts, emailAddressContacts);

            const phoneNumberContacts = lightWeightObjectCollection.PhoneNumberContacts.filter((item) =>
                this.filterLocalSearchItem(searchText, item),
            ).map((item) => ({ Id: undefined, Name: item }));
            addLocalSearchGroupFn(FiLocalSearchPropertyType.PhoneNumberContacts, phoneNumberContacts);

            const addresses = lightWeightObjectCollection.Addresses.filter((item) =>
                this.filterLocalSearchItem(searchText, item),
            ).map((item) => ({ Id: undefined, Name: item }));
            addLocalSearchGroupFn(FiLocalSearchPropertyType.Addresses, addresses);

            const dataFieldValues = lightWeightObjectCollection.DataFieldValues.filter((item) =>
                this.filterLocalSearchItem(searchText, item),
            ).map((item) => ({ Id: undefined, Name: item }));
            addLocalSearchGroupFn(FiLocalSearchPropertyType.DataFieldValues, dataFieldValues);

            const objectIds = lightWeightObjectCollection.ObjectIds.map((objectId) => ({
                Id: objectId,
                Name: objectId.toString(),
            })).filter((item) => this.filterLocalSearchItem(searchText, item.Name));
            addLocalSearchGroupFn(FiLocalSearchPropertyType.ObjectIds, objectIds);

            const mediaBankFileExtensions = lightWeightObjectCollection.MediaBankFileExtensions.filter((item) =>
                this.filterLocalSearchItem(searchText, item),
            ).map((item) => ({ Id: undefined, Name: item }));
            addLocalSearchGroupFn(FiLocalSearchPropertyType.MediaBankFileExtensions, mediaBankFileExtensions);

            const objectTypes = lightWeightObjectCollection.ObjectTypes.map((objectType) => {
                const textId = getObjectTypeNameTextId(objectType);
                return { Id: objectType, Name: this.appTextService.get(textId) };
            }).filter((item) => this.filterLocalSearchItem(searchText, item.Name));
            addLocalSearchGroupFn(FiLocalSearchPropertyType.ObjectTypeNames, objectTypes);

            const checkpoints = lightWeightObjectCollection.Checkpoints?.filter((item) =>
                this.filterLocalSearchItem(searchText, item.Name),
            );
            addLocalSearchGroupFn(FiLocalSearchPropertyType.Checkpoints, checkpoints);
        }

        return localSearchData;
    }

    showSeeMatching(searchText: string, propertyType: FiLocalSearchPropertyType): boolean {
        return searchText && this.noSeeMatchingGroups.indexOf(propertyType) === -1;
    }

    private filterLocalSearchItem(searchText: string, itemName: string): boolean {
        if (typeof searchText !== 'string') {
            searchText = '';
        }
        return !searchText || compareWithSearchText(itemName, searchText);
    }

    getGroupText(group: ILocalSearchGroup, objectType: ObjectTypes): string {
        const labelTextId = this.getGroupTextLabelId(group.propertyType, objectType);
        if (!labelTextId) {
            throw 'FI: Unknown local search property type ' + group.propertyType;
        }
        const label: string = this.appTextService.get(labelTextId);
        return label + ' (' + group.allItemsCount + ')';
    }

    getSeeMatchingText(group: ILocalSearchGroup, objectType: ObjectTypes): string {
        const labelTextId = this.getGroupTextLabelId(group.propertyType, objectType, true);
        if (!labelTextId) {
            throw 'FI: Unknown local search property type ' + group.propertyType;
        }
        const label: string = this.appTextService.get(labelTextId);
        const seeAllMatchingText: string = this.appTextService.get('common.see_all_matching');
        return seeAllMatchingText.replace('{0}', label.toLowerCase());
    }

    getGroupTextLabelId(
        propertyType: FiLocalSearchPropertyType,
        objectType: ObjectTypes,
        isSeeMatchingTextMode?: boolean,
    ): string {
        if (propertyType === FiLocalSearchPropertyType.DataFieldValues) {
            return this.dataFieldValueLabels.get(objectType) || '';
        }

        const config = this.labelTextConfig.get(propertyType);
        if (!config) {
            return '';
        }

        return isSeeMatchingTextMode ? config.plural : config.singular;
    }

    isBusinessUnitSearchItem(propertyType: FiLocalSearchPropertyType, objectType: ObjectTypes): boolean {
        return propertyType === FiLocalSearchPropertyType.Names && objectType === ObjectTypes.BusinessUnit;
    }

    isBusinessUnitIconShown(
        propertyType: FiLocalSearchPropertyType,
        physicalFileName: string,
        objectType: ObjectTypes,
    ): boolean {
        return (
            !this.isWithProfilePicture(propertyType, physicalFileName, objectType) &&
            this.isBusinessUnitSearchItem(propertyType, objectType)
        );
    }

    getProfilePictureIcon(
        propertyType: FiLocalSearchPropertyType,
        objectType: ObjectTypes,
        fileType?: FileType,
        isRegularIcon?: boolean,
    ): string {
        if (this.isBusinessUnitSearchItem(propertyType, objectType)) {
            return 'fas fa-shop BrandedColor';
        }

        if (propertyType === FiLocalSearchPropertyType.Names && objectType === ObjectTypes.MediaBank) {
            return `far ${fiGetMediaBankFileIconAwesome(fileType)}`;
        }

        if (isRegularIcon) {
            return this.propertyRegularIconConfig.get(propertyType) || '';
        }

        return this.propertyIconConfig.get(propertyType) || '';
    }

    getAddedObjectColor(propertyType: FiLocalSearchPropertyType): string {
        return this.propertyColorConfig.get(propertyType) || '#0288D1';
    }

    isWithProfilePicture(
        propertyType: FiLocalSearchPropertyType,
        physicalFileName: string,
        objectType: ObjectTypes,
    ): boolean {
        return (
            propertyType === FiLocalSearchPropertyType.Creators ||
            (propertyType === FiLocalSearchPropertyType.Names &&
                objectType === ObjectTypes.BusinessUnit &&
                !!physicalFileName)
        );
    }

    getProfileDataFromLocalSearchIdName(
        item: ILocalSearchIdNameWithPicture,
        propertyType: FiLocalSearchPropertyType,
        objectType: ObjectTypes,
    ): IUserProfile | undefined {
        return this.isWithProfilePicture(propertyType, item.PhysicalFileName, objectType)
            ? {
                  Id: item.Id,
                  FullName: item.Name,
                  MediaBankPhysicalFileName: item.PhysicalFileName,
                  MediaBankExtension: item.Extension,
              }
            : undefined;
    }

    getProfileDataFromAddedObject(item: IAddedObject, objectType: ObjectTypes): IUserProfile | undefined {
        return this.isWithProfilePicture(item.propertyType, item.profilePicture, objectType)
            ? {
                  Id: item.id,
                  FullName: item.name,
                  MediaBankPhysicalFileName: item.profilePicture,
                  MediaBankExtension: item.profilePictureExtension,
              }
            : undefined;
    }

    getSearchInputPlaceholder(isPlaceHolderVisible: boolean): string {
        return isPlaceHolderVisible ? this.appTextService.get('common.search') : '';
    }

    getEmptySearchModel(): ILocalSearchParameter {
        return {
            Names: [],
            TagIds: [],
            CreatedByUserNames: [],
            CreatedByUserIds: [],
            BusinessAreaIds: [],
            RoleIds: [],
            BusinessUnitIds: [],
            AreaIds: [],
            ClusterIds: [],
            Contacts: [],
            Addresses: [],
            DataFieldValues: [],
            ChecklistNames: [],
            CheckpointNames: [],
            ContentSearchTexts: [],
            ObjectIds: [],
            ObjectTypes: [],
            MediaBankFileExtensions: [],
        };
    }

    buildSearchModelFromAddedObjects(
        addedObjects: IAddedObject[],
        currentObjectType: ObjectTypes,
    ): ILocalSearchParameter {
        const searchModel = this.getEmptySearchModel();

        addedObjects.forEach((addedObject) => {
            this.mapAddedObjectToSearchModel(addedObject, searchModel, currentObjectType);
        });

        return searchModel;
    }

    private mapAddedObjectToSearchModel(
        addedObject: IAddedObject,
        searchModel: ILocalSearchParameter,
        currentObjectType: ObjectTypes,
    ): void {
        if (addedObject.propertyType === FiLocalSearchPropertyType.Creators) {
            this.mapCreatorToSearchModel(addedObject, searchModel, currentObjectType);
            return;
        }

        const config = this.propertyMappingConfig.get(addedObject.propertyType);
        if (!config) {
            return;
        }

        const value = addedObject[config.valueType];
        const targetArray = searchModel[config.modelProperty] as Array<number | string>;
        targetArray.push(value);
    }

    private mapCreatorToSearchModel(
        addedObject: IAddedObject,
        searchModel: ILocalSearchParameter,
        currentObjectType: ObjectTypes,
    ): void {
        const supportsIdSearch = this.objectTypesWithIdSearch.includes(currentObjectType);

        if (!supportsIdSearch) {
            // legacy search by CreatedByUserName
            searchModel.CreatedByUserNames.push(addedObject.name);
            return;
        }

        if (addedObject.id) {
            // search by CreatedByUserId
            searchModel.CreatedByUserIds.push(addedObject.id);
        } else {
            // see all matching creators (search by substring)
            searchModel.CreatedByUserNames.push(addedObject.name);
        }
    }

    getNewAddedObject(item: ILocalSearchIdNameWithPicture, propertyType: FiLocalSearchPropertyType): IAddedObject {
        return {
            propertyType: propertyType,
            id: item.Id,
            name: item.Name,
            profilePicture: item.PhysicalFileName,
            profilePictureExtension: item.Extension,
            fileType: item.FileType,
        };
    }

    //#region LocalSearchParameter props matching
    isNameMatching(name: string, localSearchParameter: ILocalSearchParameter): boolean {
        return this.isStringPropMatching(name, localSearchParameter.Names);
    }
    isCreatedByMatching(createdBy: string, localSearchParameter: ILocalSearchParameter): boolean {
        return this.isStringPropMatching(createdBy, localSearchParameter.CreatedByUserNames);
    }
    private isStringPropMatching(prop: string, searchStrings: string[]): boolean {
        const normalizedProp = (prop ?? '').toLowerCase();
        return !searchStrings.find((searchString) => !normalizedProp.includes(searchString.toLowerCase()));
    }
    //#endregion
}
